import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'
import {
  type BusinessTax,
  type Field,
  type FieldType,
  NumberUtils,
  PermissionArea,
  Text,
  TextWithTooltip,
  useFields,
} from '@pbt/pbt-ui-components'

import { usePracticeFieldsSection } from '~/components/dashboard/admin/general/practices/hooks/usePracticeFieldsSection'
import TaxConfigurationNameRateRow from '~/components/dashboard/admin/general/practices/PracticeDetailsSections/tax-configuration/TaxConfigurationNameRateRow'
import FeatureToggle from '~/constants/featureToggle'
import { getCRUDByAreaForBusiness } from '~/store/reducers/auth'
import { getFeatureToggleForBusiness } from '~/store/reducers/constants'
import type {
  BasePracticeDetailsSectionProps,
  PracticeTaxConfigurationFields,
} from '~/types'

import { PracticeDetailsPanels } from '../../practices'

const taxLineFieldProps = (
  lines: BusinessTax[],
  namePrefix: string,
  ratePrefix: string,
) =>
  R.flatten(
    lines.map((line) => [
      { name: `${namePrefix}_${line.id}`, initialValue: line.name },
      {
        name: `${ratePrefix}_${line.id}`,
        initialValue: line.rateValue,
        type: 'number' as FieldType,
      },
    ]),
  )

const parsedTaxLinesFromFields = (
  fields: Record<string, Field>,
  lines: BusinessTax[],
  nameKey: (id: string) => string,
  rateKey: (id: string) => string,
  options?: { preTaxFee?: boolean },
) =>
  lines.map((line) => ({
    ...line,
    name: fields[nameKey(line.id)].value,
    rateValue: Number(fields[rateKey(line.id)].value),
    ...(options?.preTaxFee ? { preTaxFee: true as const } : {}),
  }))

const useStyles = makeStyles(
  (theme) => ({
    taxRateContainerBordered: {
      borderBottom: theme.constants.filterBorder,
    },
    totalTaxRateContainer: {
      height: 45,
    },
  }),
  { name: 'TaxConfigurationSection' },
)

const TaxConfigurationSection = ({
  business,
}: BasePracticeDetailsSectionProps) => {
  const classes = useStyles()
  const { t } = useTranslation('Businesses')

  const permissions = useSelector(
    getCRUDByAreaForBusiness(PermissionArea.BUSINESS, business),
  )

  const isPreTaxFeeCollectionEnabled = useSelector(
    getFeatureToggleForBusiness(
      FeatureToggle.PRE_TAX_FEE_COLLECTION,
      business.id,
    ),
  )

  const taxes = (business.taxes || []).filter((tax) => !tax.preTaxFee)
  const preTaxFees = isPreTaxFeeCollectionEnabled
    ? (business.taxes || []).filter((tax) => tax.preTaxFee)
    : []

  const taxFieldProps = taxLineFieldProps(taxes, 'taxName', 'taxRateValue')
  const preTaxFeeFieldProps = isPreTaxFeeCollectionEnabled
    ? taxLineFieldProps(preTaxFees, 'preTaxFeeName', 'preTaxFeeRateValue')
    : []

  const { fields, validate, reset } = useFields(
    [...taxFieldProps, ...preTaxFeeFieldProps],
    false,
  )

  const taxRateValueFields = Object.values(fields).filter((field) =>
    field.name.startsWith('taxRateValue'),
  )

  const totalTaxRate = R.sum(R.pluck('value', taxRateValueFields))

  usePracticeFieldsSection<PracticeTaxConfigurationFields>({
    business,
    fields,
    parsedFields: {
      taxes: [
        ...parsedTaxLinesFromFields(
          fields,
          taxes,
          (id) => `taxName_${id}`,
          (id) => `taxRateValue_${id}`,
        ),
        ...(isPreTaxFeeCollectionEnabled
          ? parsedTaxLinesFromFields(
              fields,
              preTaxFees,
              (id) => `preTaxFeeName_${id}`,
              (id) => `preTaxFeeRateValue_${id}`,
              { preTaxFee: true },
            )
          : []),
      ],
    },
    sectionName: PracticeDetailsPanels.TAX_CONFIGURATION,
    validate,
    reset,
  })

  return (
    <>
      <Grid item mb={2}>
        <TextWithTooltip
          allowWrap
          strong
          tooltipText={t(
            'Businesses:TAX_CONFIGURATION.UNDER_ADMIN_MENU_YOU_CAN_MARK_ITEMS_AS_TAXABLE',
          )}
          variant="subheading3"
        >
          {t('Businesses:TAX_CONFIGURATION.TAX_CONFIGURATION')}
        </TextWithTooltip>
      </Grid>
      {taxes.map((tax) => (
        <TaxConfigurationNameRateRow
          key={tax.id}
          nameField={fields[`taxName_${tax.id}`]}
          rateDisabled={!permissions.update}
          rateField={fields[`taxRateValue_${tax.id}`]}
        />
      ))}
      <Grid
        container
        item
        alignItems="center"
        className={classes.totalTaxRateContainer}
        justifyContent="space-between"
        xs={6}
      >
        <Text strong variant="body">
          {t('Businesses:TAX_CONFIGURATION.TOTAL_TAX_RATE')}
        </Text>
        {totalTaxRate > 0 && (
          <Text strong sx={{ width: 100 }} variant="body">
            {NumberUtils.toPercentFormat(totalTaxRate, 2)}
          </Text>
        )}
      </Grid>
      {isPreTaxFeeCollectionEnabled && preTaxFees.length > 0 && (
        <>
          <Grid item mb={2} mt={3}>
            <TextWithTooltip
              allowWrap
              strong
              tooltipText={t(
                'Businesses:TAX_CONFIGURATION.PRE_TAX_FEES_TOOLTIP',
              )}
              variant="subheading3"
            >
              {t('Businesses:TAX_CONFIGURATION.PRE_TAX_FEES')}
            </TextWithTooltip>
          </Grid>
          {preTaxFees.map((tax) => (
            <TaxConfigurationNameRateRow
              key={tax.id}
              nameField={fields[`preTaxFeeName_${tax.id}`]}
              rateDisabled={!permissions.update}
              rateField={fields[`preTaxFeeRateValue_${tax.id}`]}
            />
          ))}
        </>
      )}
    </>
  )
}

export default memo(TaxConfigurationSection, (prevProps, nextProps) =>
  R.equals(prevProps.business, nextProps.business),
)
